import {
  EContentSectionType,
  EMoveDirection,
  EUrlButtonType,
  type TMessageSection,
  type TMediaSection,
} from 'types';
import { Icon, IconName } from '../../../components/Icon';
import {
  autoReplyMessageStrings,
  sendAllMessageStrings,
} from '../../../constants';
import { cn } from '../../../utils/cn';
import { useCallback } from 'react';

const icons = sendAllMessageStrings.icons;
const contentHeaderStrings = sendAllMessageStrings.contentHeader;

export type ContentSectionVariant =
  | 'bulk'
  | 'autoReply'
  | 'messageSettings'
  | 'greetingMessageSettings';

type Props = {
  section: TMessageSection;
  index: number;
  totalSections: number;
  onMove: (direction: EMoveDirection) => void;
  onDelete: () => void;
  onTypeSelect: (type: EContentSectionType | EUrlButtonType) => void;
  hasContentFlag: boolean;
  hideActionUrl?: boolean;
  variant?: ContentSectionVariant;
  /**
   * PDF(FILE)セクションを選べるようにするか。
   * PDFは「挨拶メッセージを個別チャットにも送信する」設定のときしか配信経路が無いため、
   * 呼び出し側で条件を判定して渡す（サーバ側でも同じ条件で弾いている）。
   */
  allowFileSection?: boolean;
  /** アンケート機能がOFFの組織ではアンケートセクションを選べなくする */
  allowSurveySection?: boolean;
};

const contentHeaderClasses = {
  container: 'flex items-center justify-between p-2',
  typeList: 'flex flex-wrap gap-2',
  typeButton: {
    base: 'flex h-10 w-10 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50',
    selected: 'bg-primary-200 text-white',
    unselected: 'bg-neutral-200 hover:bg-neutral-300',
  },
  controls: 'z-10 flex gap-1',
  controlButton:
    'flex h-7 w-7 items-center justify-center rounded-full bg-neutral-200 transition hover:bg-neutral-300 disabled:cursor-not-allowed disabled:opacity-50',
};

const urlButtonClasses = {
  base: 'flex h-10 w-10 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50',
  selected: 'bg-primary-200 text-white',
  unselected: 'bg-neutral-200 hover:bg-neutral-300',
};

const MESSAGE_SETTINGS_CONTENT_TYPES: Array<{
  type: TMessageSection['type'];
  icon: string;
  label: string;
}> = [
  {
    type: EContentSectionType.TEXT,
    icon: icons.iconNames.text,
    label: icons.iconLabels.text,
  },
  {
    type: EContentSectionType.IMAGE,
    icon: icons.iconNames.image,
    label: icons.iconLabels.image,
  },
  {
    type: EContentSectionType.VIDEO,
    icon: icons.iconNames.video,
    label: icons.iconLabels.video,
  },
];

// FILE は配信経路が「挨拶 + 個別チャットにも送信ON」に限られるため、既定の一覧に含めない
const FILE_CONTENT_TYPE: {
  type: TMessageSection['type'];
  icon: string;
  label: string;
} = {
  type: EContentSectionType.FILE,
  icon: icons.iconNames.file,
  label: icons.iconLabels.file,
};

const CARD_TYPE_MESSAGE_CONTENT_TYPE: {
  type: TMessageSection['type'];
  icon: string;
  label: string;
} = {
  type: EContentSectionType.CARD_TYPE_MESSAGE,
  icon: 'CT',
  label: autoReplyMessageStrings.contentTypes.cardTypeMessage,
};

const SURVEY_CONTENT_TYPE: {
  type: TMessageSection['type'];
  icon: string;
  label: string;
} = {
  type: EContentSectionType.SURVEY,
  icon: 'SV',
  label: 'アンケート',
};

const GREETING_MESSAGE_SETTINGS_CONTENT_TYPES: Array<{
  type: TMessageSection['type'];
  icon: string;
  label: string;
}> = [
  ...MESSAGE_SETTINGS_CONTENT_TYPES,
  CARD_TYPE_MESSAGE_CONTENT_TYPE,
  SURVEY_CONTENT_TYPE,
];

const BULK_CONTENT_TYPES: Array<{
  type: TMessageSection['type'];
  icon: string;
  label: string;
}> = [
  {
    type: EContentSectionType.TEXT,
    icon: icons.iconNames.text,
    label: icons.iconLabels.text,
  },
  {
    type: EContentSectionType.IMAGE,
    icon: icons.iconNames.image,
    label: icons.iconLabels.image,
  },
  {
    type: EContentSectionType.VIDEO,
    icon: icons.iconNames.video,
    label: icons.iconLabels.video,
  },
  {
    type: EContentSectionType.RICH_MESSAGE,
    icon: 'RM',
    label: autoReplyMessageStrings.contentTypes.richMessage,
  },
  CARD_TYPE_MESSAGE_CONTENT_TYPE,
  SURVEY_CONTENT_TYPE,
];

const AUTO_REPLY_CONTENT_LABELS: Partial<Record<TMessageSection['type'], string>> =
  autoReplyMessageStrings.contentTypes;
// 選択可能な種別は一斉送信と共通にし、自動応答の表示名だけを差し替える。
const AUTO_REPLY_CONTENT_TYPES = BULK_CONTENT_TYPES.map((contentType) => ({
  ...contentType,
  label: AUTO_REPLY_CONTENT_LABELS[contentType.type] ?? contentType.label,
}));

const URL_BUTTONS: Array<{
  type: EUrlButtonType;
  icon: IconName;
  label: string;
  relatedType: EContentSectionType;
}> = [
  {
    type: EUrlButtonType.IMAGE_URL,
    icon: icons.iconNames.imageUrl,
    label: icons.iconLabels.imageUrl,
    relatedType: EContentSectionType.IMAGE,
  },
  {
    type: EUrlButtonType.VIDEO_URL,
    icon: icons.iconNames.videoUrl,
    label: icons.iconLabels.videoUrl,
    relatedType: EContentSectionType.VIDEO,
  },
];

export function ContentHeader({
  section,
  index,
  totalSections,
  onMove,
  onDelete,
  onTypeSelect,
  hasContentFlag,
  hideActionUrl = false,
  variant = 'bulk',
  allowFileSection = false,
  allowSurveySection = true,
}: Props) {
  let contentTypes = BULK_CONTENT_TYPES
  switch (variant) {
    case 'autoReply':
      contentTypes = AUTO_REPLY_CONTENT_TYPES
      break
    case 'messageSettings':
      contentTypes = MESSAGE_SETTINGS_CONTENT_TYPES
      break
    case 'greetingMessageSettings':
      contentTypes = allowFileSection
        ? [
            ...MESSAGE_SETTINGS_CONTENT_TYPES,
            FILE_CONTENT_TYPE,
            CARD_TYPE_MESSAGE_CONTENT_TYPE,
            SURVEY_CONTENT_TYPE,
          ]
        : GREETING_MESSAGE_SETTINGS_CONTENT_TYPES
      break
  }

  const selectableContentTypes = allowSurveySection
    ? contentTypes
    : contentTypes.filter(
        (contentType) => contentType.type !== EContentSectionType.SURVEY,
      )

  const renderContentTypeButtons = useCallback(() => {
    return selectableContentTypes.map((contentType) => {
      const isSelected = section.type === contentType.type;
      const isDisabled = !isSelected && hasContentFlag;

      return (
        <button
          key={contentType.type}
          type="button"
          onClick={() => onTypeSelect(contentType.type)}
          disabled={isDisabled}
          className={cn(
            contentHeaderClasses.typeButton.base,
            isSelected
              ? contentHeaderClasses.typeButton.selected
              : contentHeaderClasses.typeButton.unselected,
          )}
          title={contentType.label}
        >
          {contentType.icon === 'T' ||
          contentType.icon === 'PDF' ||
          contentType.icon === 'RM' ||
          contentType.icon === 'CT' ||
          contentType.icon === 'SV' ? (
            <span className="text-xs font-bold">{contentType.icon}</span>
          ) : (
            <Icon
              icon={contentType.icon as IconName}
              width={20}
              height={20}
              color={isSelected ? 'white' : undefined}
            />
          )}
        </button>
      );
    });
  }, [selectableContentTypes, section.type, hasContentFlag, onTypeSelect]);

  const renderUrlButtons = useCallback(() => {
    if (
      section.type !== EContentSectionType.IMAGE &&
      section.type !== EContentSectionType.VIDEO
    )
      return null;
    const mediaSection = section as TMediaSection;

    return URL_BUTTONS.map((urlButton) => {
      const isRelatedTypeSelected = section.type === urlButton.relatedType;
      const isSelected =
        mediaSection.showUrlInput && section.type === urlButton.relatedType;
      const isDisabled = !isRelatedTypeSelected;

      return (
        <button
          key={urlButton.type}
          onClick={() => onTypeSelect(urlButton.type)}
          disabled={isDisabled}
          className={cn(
            urlButtonClasses.base,
            isSelected
              ? urlButtonClasses.selected
              : urlButtonClasses.unselected,
          )}
          title={urlButton.label}
        >
          <Icon
            icon={urlButton.icon}
            width={20}
            height={20}
            color={isSelected ? 'white' : undefined}
          />
        </button>
      );
    });
  }, [onTypeSelect, section]);

  return (
    <div className={contentHeaderClasses.container}>
      <div className={contentHeaderClasses.typeList}>
        {renderContentTypeButtons()}
        {variant === 'bulk' && !hideActionUrl && renderUrlButtons()}
      </div>

      <div className={contentHeaderClasses.controls}>
        <button
          type="button"
          onClick={() => onMove(EMoveDirection.UP)}
          disabled={index === 0 || totalSections === 1}
          className={contentHeaderClasses.controlButton}
          title={contentHeaderStrings.moveUp}
        >
          <Icon icon="chevronUp" width={16} height={16} />
        </button>
        <button
          type="button"
          onClick={() => onMove(EMoveDirection.DOWN)}
          disabled={index === totalSections - 1 || totalSections === 1}
          className={contentHeaderClasses.controlButton}
          title={contentHeaderStrings.moveDown}
        >
          <Icon icon="chevronDown" width={16} height={16} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={totalSections === 1}
          className={contentHeaderClasses.controlButton}
          title={contentHeaderStrings.delete}
        >
          <Icon icon="close" width={18} height={18} />
        </button>
      </div>
    </div>
  );
}
